<template>
  <!--
  HINT:
    This is the only place we can use DevOnly component. Otherwise build will fail with sourceMaps error
    Use v-if="isDev" instead.
  -->
  <v-hover v-if="isDev" v-slot="{ props, isHovering }" :disabled="!show">
    <v-btn-icon
      v-tooltip="title ? { text: title, openDelay: 0 } : undefined"
      variant="plain"
      density="compact"
      icon="mdi-bug-outline"
      size="20"
      v-bind="props"
      :color="show ? 'warning' : ''"
      @click.stop="handleDebugClick"
    />

    <Teleport to="body">
      <v-fade-transition>
        <div v-if="show" class="debug-drawer px-2 py-4" :class="position">
          <div
            class="debug-drawer-content bg-glass border d-flex flex-column"
            :class="{ 'debug-drawer-content--emphasized': isHovering }"
          >
            <!-- Controls -->

            <div class="d-flex pa-2">
              <v-btn v-if="toJson" append-icon="mdi-console" @click="logToConsole()"> Log to Console </v-btn>

              <v-spacer></v-spacer>
              <v-btn-icon icon="mdi-close" variant="text" @click="show = false" />
            </div>

            <v-divider thickness="3" color="black"></v-divider>
            <small v-if="title" class="text-center">{{ title }}</small>

            <!-- Content -->
            <div ref="jsonContainer" class="overflow-auto fill-height text-small">
              <slot></slot>
            </div>
          </div>
        </div>
      </v-fade-transition>
    </Teleport>
  </v-hover>
</template>

<script lang="ts">
import { defineComponent, ref, toRaw, toValue, useTemplateRef, watch } from 'vue'
import type { PropType } from 'vue'
import { useAppStore } from '~/stores/app'
import { useEventListener, watchImmediate } from '@vueuse/core'
import { Notify } from '~/lib/notify'
import JSONFormatter from 'json-formatter-js'
import { jsonify } from '~/helper/json-formatter'
import { isDev } from '~/helper/nuxt'

export default defineComponent({
  name: 'DebugHintIcon',
  props: {
    position: {
      type: String as PropType<'left' | 'right' | 'top' | 'bottom'>,
      default: 'right',
    },
    eager: {
      type: Boolean,
      default: false,
    },
    toJson: {
      type: Object as PropType<object | undefined | null>,
      default: undefined,
      required: false,
    },
    title: {
      type: String,
      required: false,
      default: undefined,
    },
    onlyConsoleLog: {
      type: Object as PropType<object | undefined | null>,
      default: undefined,
      required: false,
    },
  },
  setup(props) {
    const appStore = useAppStore()
    const show = ref(false)
    useEventListener('keydown', (ev) => {
      if (ev.code == '27' || ev.key === 'Escape') {
        show.value = false
      }
    })
    // watch(
    //   () => props.toJson,
    //   (value) => {
    //     if (value) {
    //       logJson()
    //     }
    //   },
    //   { immediate: true },
    // )

    function handleDebugClick() {
      if (props.onlyConsoleLog) {
        Notify.info('Check console')
        console.log('DEBUG: ', toRaw(props.onlyConsoleLog))
        return
      }
      show.value = !show.value
    }

    function logToConsole(notify = false) {
      if (notify) {
        Notify.info('Check console')
      }
      console.log('DEBUG: ', toRaw(props.toJson))
    }

    const jsonContainerEl = useTemplateRef<HTMLDivElement>('jsonContainer')
    const jsonFormattedDiv = ref<HTMLDivElement | null>(null)

    watch([jsonContainerEl, jsonFormattedDiv], ([container, target]) => {
      if (container && target) {
        container?.appendChild(target)
      }
    })

    const formatter = new JSONFormatter({}, 2)
    function renderJSON(json: object | null | undefined) {
      formatter.json = jsonify(toValue(json || {}))
      jsonFormattedDiv.value?.remove()
      jsonFormattedDiv.value = formatter.render()
    }
    const { pause, resume } = watch(() => props.toJson, renderJSON, {
      deep: true,
    })

    watchImmediate(show, (value) => {
      if (value) {
        renderJSON(props.toJson)
        resume()
      } else {
        pause()
      }
    })

    return {
      isDev,
      jsonFormattedDiv,
      show,
      appStore,
      handleDebugClick,
      logToConsole: logToConsole,
    }
  },
  data() {
    return {}
  },
  mounted(): any {
    if (this.eager) {
      this.show = true
    }
  },
})
</script>

<style scoped lang="scss">
.debug-drawer-content {
  position: relative;
  overflow: hidden;
  height: 100%;
  border-radius: 1rem;
  transition: transform 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  will-change: transform;
}
.debug-drawer-content--emphasized {
  transform: scale(1.01);
}
.debug-drawer {
  resize: both;
  overflow: hidden;
  z-index: 999999;
  position: fixed;
  --size: 30;
  &.right {
    right: 0;
    top: 0;
    bottom: 0;
    width: calc(1vw * var(--size));
  }
  &.left {
    left: 0;
    top: 0;
    bottom: 0;
    width: calc(1vw * var(--size));
  }
  &.bottom {
    left: 0;
    right: 0;
    bottom: 0;
    width: calc(1vh * var(--size));
  }
  &.top {
    left: 0;
    right: 0;
    top: 0;
    width: calc(1vh * var(--size));
  }
}
</style>
